// utils/shipmentWeight.ts — SATU-SATUNYA rumus berat paket (sumber: Daftar Produk).
// totalGrams = unitGrams × qty + Σ bumpGrams · billedKg = max(1, ceil(total/1000)).
// Berlaku SAMA untuk Everpro/Mengantar/Lincah/Biteship (<1kg tetap 1kg).
export interface WeightBreakdown {
  totalGrams: number;
  billedKg: number;
  billedGrams: number;
  label: string;
}

const toNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function calcBilledWeight(args: {
  unitGrams?: unknown;
  qty?: unknown;
  bumpGrams?: Array<unknown>;
}): WeightBreakdown {
  const unit = toNum(args.unitGrams) > 0 ? toNum(args.unitGrams) : 1000;
  const qty = Math.max(1, Math.floor(toNum(args.qty)) || 1);
  const bumps = Array.isArray(args.bumpGrams) ? args.bumpGrams : [];
  const bumpSum = bumps.reduce<number>((s, b) => s + Math.max(0, toNum(b)), 0);
  const totalGrams = Math.round(unit * qty + bumpSum);
  const billedKg = Math.max(1, Math.ceil(totalGrams / 1000));
  const parts: string[] = [`${qty}×${unit}g`];
  if (bumpSum > 0) parts.push(`bump ${bumpSum}g`);
  const label = `${parts.join(' + ')} = ${totalGrams}g → ${billedKg}kg`;
  return { totalGrams, billedKg, billedGrams: billedKg * 1000, label };
}

// Multi-varian: order.variant bisa gabungan ("M, XL") sementara berat tercatat
// per-varian (products.attributes.variants[].weight / form.variantCombinations).
// Insiden 2026-10-27 (#5225a8c7): 2×220g tampil "2×1000g = 2000g → 2kg" karena
// resolver lama tak pernah baca berat varian (fallback buta 1000g).
export interface VariantWeightCandidate {
  name: unknown;
  weight: unknown;
}

// Pecah label gabungan — HANYA koma/plus/ampersand. Slash DIKECUALIKAN karena
// label combo tunggal memakai " / " ("Hitam / XL" = 1 varian, bukan 2).
export function splitVariantParts(label: unknown): string[] {
  return String(label ?? '')
    .split(/[,+&]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface VariantWeightResolution {
  avg: number;
  matched: number;
  parts: number;
}

// Rata-rata berat varian yang cocok (exact, case-insensitive). avg=0 bila tak
// ada yang cocok — caller WAJIB pakai fallback lama (jangan 0).
export function resolveVariantUnitGrams(
  variantLabel: unknown,
  candidates: VariantWeightCandidate[],
): VariantWeightResolution {
  const parts = splitVariantParts(variantLabel);
  if (!parts.length || !candidates.length) return { avg: 0, matched: 0, parts: parts.length };
  const norm = (s: unknown) => String(s ?? '').trim().toLowerCase();
  let sum = 0;
  let matched = 0;
  for (const p of parts) {
    const hit = candidates.find((c) => norm(c.name) === p.toLowerCase())
      // Label legacy gabungan dimensi yang sudah dihapus ("AREA / XXL"):
      // coba tiap token pisahan '/' supaya ukuran lama tetap ketemu (150g, bukan 1000g).
      || candidates.find((c) => String(p).split('/').map((t) => t.trim().toLowerCase()).filter(Boolean).some((t) => norm(c.name) === t));
    const w = hit ? Number(hit.weight) || 0 : 0;
    if (w > 0) {
      sum += w;
      matched++;
    }
  }
  return { avg: matched ? sum / matched : 0, matched, parts: parts.length };
}

export function billedKgFromGrams(totalGrams: unknown): number {
  const t = toNum(totalGrams) > 0 ? toNum(totalGrams) : 1000;
  return Math.max(1, Math.ceil(t / 1000));
}

export function formatWeightLine(w: WeightBreakdown): string {
  return `Berat: ${w.label}`;
}
